import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Grid,
  Typography,
  Alert,
} from "@mui/material";

import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import OnboardingSteps from "../../../components/Steps/OnboardingSteps";
import ButtonsMainGoTo from "../../../components/Button/ButtonMainGoTo";

import * as c from "../../../constants";
import { pool } from "../../../constants";

import SpeakStart_de from "./SpeakStart_de";
import SpeakStart_en from "./SpeakStart_en";
import BiomaStart_de from "./BiomaStart_de";
import BiomaStart_en from "./BiomaStart_en";


export default function OnbQuest() {
  const { state, handleLogoutapi } = useAuth();
  const { t, language, changeLanguage } = useTranslation();

  // -----------------------------
  // Core state
  // -----------------------------
  const [visit, setVisit] = useState(null);
  const [finalizing, setFinalizing] = useState(true);
  const [pageError, setPageError] = useState(null);

  // -----------------------------
  // Kit state
  // -----------------------------
  const [kit, setKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(true);
  const [kitError, setKitError] = useState(null);

  useEffect(() => {
    if (state.user?.language) {
      changeLanguage(state.user.language);
    }
  }, [state.user?.language]);

  // -----------------------------
  // Fetch current lab visit (visit_number = 1)
  // -----------------------------
  useEffect(() => {
    if (!state?.user?.id) return;

    axios
      .get(`${c.API_URL}/api/current-lab-visit/`)
      .then((res) => setVisit(res.data))
      .catch(() => setPageError(t("onb_quest_err_no_visit")));
  }, [state?.user?.id]);

  // -----------------------------
  // lab visit --> send email summary
  // -----------------------------
  useEffect(() => {
  if (!visit?.id) return;

  const finalizeVisit = async () => {
    try {
      await axios.post(`${c.API_URL}/api/lab-visits/send_email/`);
      setFinalizing(false);
    } catch (err) {
      console.error(err);
      setPageError(t("onb_quest_err_finalize"));
    }
  };

  finalizeVisit();
}, [visit?.id]);

  // -----------------------------
  // 5️⃣ Fetch latest microbiome kit - conditional
  // -----------------------------
  useEffect(() => {
    if (!visit?.has_microbiome) return; // early return

    let mounted = true;

    const fetchLatestKit = async () => {
      try {
        setKitLoading(true);
        const res = await axios.get(
          `${c.API_URL}/api/stool-sample-kits/my-latest-kit/`
        );

        if (mounted) {
          setKit(res.data);
          setKitError(null);
        }
      } catch (err) {
        if (!mounted) return;

        if (err.response?.status === 404) {
          setKitError(t("onb_quest_err_no_kit"));
        } else {
          setKitError(t("onb_quest_err_kit_load"));
        }
      } finally {
        mounted && setKitLoading(false);
      }
    };

    fetchLatestKit();
    return () => {
      mounted = false;
    };
  }, []);

  // -----------------------------
  // Early UI states (SAFE)
  // -----------------------------
  if (finalizing) {
    return (
      <Grid container justifyContent="center" mt={6}>
        <CircularProgress />
      </Grid>
    );
  }

  if (pageError) {
    return (
      <Grid container justifyContent="center" mt={6} sx={{ px: { xs: 2, sm: 0 } }}>
        <Alert severity="error">{pageError}</Alert>
      </Grid>
    );
  }


  return (
  
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext="Preventive Health Intelligence"
      headline={t("onb_subscr26")}
      content={
        <>
          {/* Steps */}
          <Grid container sx={{ mt: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Grid size={12} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <OnboardingSteps stepNumber={3} />
            </Grid>
          </Grid>

          {/* Confirmation */}
          <Grid container justifyContent="center" mt={4}>
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 3, sm: 0 } }}>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li">{t("onb_subscr28")}</Typography>
                <Typography component="li">{t("onb_subscr29")}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Conditional onboarding */}
          {visit?.has_microbiome ? (
            <>
              <Typography textAlign="center" mt={4} sx={{ px: { xs: 3, sm: 0 } }}>
                {t("onb_quest3")}
                <br />
                {t("onb_quest35")}
              </Typography>

              <Grid container justifyContent="center" my={4} gap={4}>

                <Grid size={{ xs: 12 }} sx={{ maxWidth: 240, mx: 'auto' }}>
                  {kitLoading ? (
                    <Stack alignItems="center">
                      <CircularProgress />
                    </Stack>
                  ) : kitError ? (
                    <Alert severity="info">{kitError}</Alert>
                  ) : kit ? (
                    <Card sx={{borderRadius: 4}}>
                      <CardContent >
                        <Typography variant="h6" textAlign="center" mb={2}>
                          {t("onb_quest36")}
                        </Typography>

                        <Typography variant="subtitle2" textAlign="center" fontWeight={400} sx={{px:2}}>
                          {t("onb_quest_kit_id")}
                        </Typography>
                        <Typography fontWeight={400} textAlign="center" sx={{px:2}}>
                          {kit.kitId}
                        </Typography>

                        <Typography variant="subtitle2" textAlign="center" fontWeight={400} mt={2} sx={{px:2}}>
                          {t("onb_quest_act_key")}
                        </Typography>
                        <Typography fontWeight={400} textAlign="center" sx={{px:2}}>
                          {kit.activationKey}
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : null}
                </Grid>
              
              <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 3, sm: 0 } }}>
                <Typography textAlign="center" my={1}>
                  {t("onb_quest37")}
                  <br />
                  {t("onb_quest38")}
                  <br />
                  {t("onb_quest39")}
                </Typography>
              </Grid>

               <Grid container spacing={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                   
                          <ButtonsMainGoTo name="LOGOUT" to="/Login" onClick={handleLogoutapi}/>
                     </Grid> 
                  </Grid>

                  
             {/* Questionnaire intro */}
              <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 3, sm: 0 } }}>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  <Typography component="li">{t("onb_quest41")}</Typography>
                  <Typography component="li">{t("onb_quest42")}</Typography>
                  <Typography component="li">{t("onb_quest4")}</Typography>
                  <Typography component="li">{t("onb_quest5")}</Typography>
                </Box>
              </Grid>

                <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 0 } }}>
                  {language === "en" && <BiomaStart_en />}
                  {language === "de" && <BiomaStart_de />}
                </Grid>

              </Grid>
            </>
          ) : (
            <>
              <Grid container justifyContent="center" mt={4}>
                <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 3, sm: 0 } }}>
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    <Typography component="li">{t("onb_quest1")}</Typography>
                    <Typography component="li">{t("onb_quest2")}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Grid container justifyContent="center" my={8}>
                <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 0 } }}>
                  {language === "en" && <SpeakStart_en />}
                  {language === "de" && <SpeakStart_de />}
                </Grid>
              </Grid>
            </>
          )}
        </>
      }
    />
  );
}
